import { defineStore } from 'pinia'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { BUNDLED_BRAND, NEUTRAL_BRAND } from '@/globals/typescript/utils/brandConfig'
import { SUPPORTED_LOCALES, type TAppLocale } from '@/locales'
import { getTextObjValue } from '@team-milva/lab-models/textObj'
import type { ITextObj } from '@team-milva/lab-models/textObj'
import type { IImageBase } from '@team-milva/lab-models/image'
import type { ILabAccountPublic } from '@team-milva/lab-models/labAccountPublic'

function isSupportedLocale(value: string | undefined): value is TAppLocale {
  return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

/**
 * Pre-login branding — see the "Per-Lab Branding for the Customer App" plan.
 *
 * Not auth-gated (works before any user exists), so this deliberately skips the
 * `resetStore()` convention that auth-gated stores use. `resolveBrand()` is a one-shot
 * `getDoc`, not an `onSnapshot` listener — branding changes rarely, and a live listener
 * held open for the entire pre-login session is unnecessary cost for that.
 */
export const useBrandStore = defineStore('brand', {
  state: () => ({
    displayName: BUNDLED_BRAND.displayName as ITextObj,
    logo: null as IImageBase | null,
    defaultLocale: BUNDLED_BRAND.defaultLocale as TAppLocale,
    // Empty until the live document resolves — the build-time config has no
    // equivalent of this (a whole list, not a single default), so there's no
    // bundled guess to seed it with. An empty list means "unknown yet, don't
    // narrow" to every consumer (see LangSelect.vue), the same as a build with
    // no brand configured at all.
    customerLocales: [] as string[],
    isResolved: false,
  }),

  getters: {
    // Routes ITextObj resolution through the store rather than calling
    // getTextObjValue directly in a component — matches how every other
    // ITextObj field in this codebase is resolved via a model/store method.
    getDisplayName: (state) => (locale: string) => getTextObjValue(state.displayName, locale),
  },

  actions: {
    /**
     * Confirms (or corrects) the bundled first-paint guess against the live document.
     * Never awaited before mount — see `main.ts` — so a slow or offline fetch cannot
     * delay first paint; the bundled guess stays on screen until this resolves.
     */
    async resolveBrand() {
      if (!BUNDLED_BRAND.slug) {
        this.isResolved = true
        return
      }

      try {
        const snap = await getDoc(doc(db, 'PublicLabAccounts', BUNDLED_BRAND.slug))

        if (snap.exists()) {
          const data = snap.data() as ILabAccountPublic
          this.displayName = data.displayName
          this.logo = data.logo
          this.defaultLocale = isSupportedLocale(data.defaultLocale)
            ? data.defaultLocale
            : NEUTRAL_BRAND.defaultLocale
          this.customerLocales = data.customerLocales ?? []
        } else {
          // The configured slug doesn't resolve to a live public document — unknown,
          // inactive, or archived lab. The bundled guess assumed the slug was valid, so
          // once that's disproved it falls to the real neutral default, not the guess.
          this.displayName = NEUTRAL_BRAND.displayName
          this.logo = null
          this.defaultLocale = NEUTRAL_BRAND.defaultLocale
          this.customerLocales = []
        }
      } catch {
        // Network/offline failure — we don't know whether the slug is valid, so trust
        // the build-time assumption rather than downgrading to the neutral default.
      } finally {
        this.isResolved = true
      }
    },
  },
})
